/**
 * @file React hook — useA2Prefill.ts: form/state logic for contributor dashboard.
 */

import { FormSubSection } from "../../../../../../interfaces/formInterfaces";
import { materialIndexForVehicleFlatRow } from "../utils/a2VehicleLayout";
import { materialIndexForFuelFlatRow } from "../utils/a2FuelLayout";
import {
  readA2FuelApiValueForField,
  readA2VehicleApiValueForField,
} from "../utils/a2ApiFormPrefill";
import { formatEmbodiedCarbonReferenceQtyFromUnknown, formatEmbodiedCarbonFunctionalUnitFromUnknown } from "../../shared/embodiedCarbonReferenceQtyFormat";

/** Type definition — Raw Mat. */
export type RawMat = {
  /** Catalog `raw_materials.id` for form controls / API FK when known. */
  id: number;
  /** Resolved catalog FK; may be null if only a background row id was present. */
  catalogId: number | null;
  name: string;
  qty?: string;
  unitId?: number;
  idx: number;
};

/** Hook: use A2Prefill. */
export function useA2Prefill() {
  const prefillFromSubmission = (
    subSections: FormSubSection[],
    submission: any,
    normalizedRaw: RawMat[],
    setValues: (updater: any) => void,
    setSectionRows: (
      rows:
        | Record<string, number>
        | ((prev: Record<string, number>) => Record<string, number>)
    ) => void,
    /** Only treat saved rawMaterialId as catalog id if it appears in this set. */
    validCatalogRawMaterialIds?: Set<number> | null,
    /** Multiple vehicles per raw material: flat list + counts per material index. */
    vehiclePrefill?: {
      counts: number[];
      flatItems: Record<string, unknown>[];
    } | null,
    /** Material-level transport fuels (max 3 per raw material); optional nested `energy` per item. */
    fuelPrefill?: {
      counts: number[];
      flatItems: Record<string, unknown>[];
    } | null
  ) => {
    const s = (v: any) =>
      v === null || v === undefined ? "" : String(v);

    const nextValues: Record<string, string> = {};
    const nextRows: Record<string, number> = {};

    const backendMap: Record<string, string> = {
      A2_TRANSPORT_DETAILS: "A2_Transport",
      A2_VEHICLE_DETAILS: "A2_Vehicle",
      A2_FUEL_TRANSPORT: "A2_Fuel",
      A2_EMBODIED_ENERGY: "A2_Energy",
      A2_EMBODIED_CARBON: "A2_Carbon",
    };

    subSections.forEach((sec) => {
      const code = sec.code!;
      const backendArr = submission?.[backendMap[code]] || [];

      if (code === "A2_EMBODIED_ENERGY" && fuelPrefill) {
        nextRows[code] = 0;
        return;
      }

      let rows: number;
      let getItem: (i: number) => Record<string, unknown>;
      let getRm: (i: number) => RawMat;

      if (code === "A2_VEHICLE_DETAILS" && vehiclePrefill) {
        rows = Math.max(1, vehiclePrefill.flatItems.length);
        getItem = (i) => vehiclePrefill.flatItems[i] ?? {};
        getRm = (i) => {
          const mi = materialIndexForVehicleFlatRow(i, vehiclePrefill.counts);
          return normalizedRaw[mi] ?? normalizedRaw[0];
        };
      } else if (code === "A2_FUEL_TRANSPORT" && fuelPrefill) {
        rows = Math.max(1, fuelPrefill.flatItems.length);
        getItem = (i) => fuelPrefill.flatItems[i] ?? {};
        getRm = (i) => {
          const mi = materialIndexForFuelFlatRow(i, fuelPrefill.counts);
          return normalizedRaw[mi] ?? normalizedRaw[0];
        };
      } else {
        rows =
          backendArr.length > 0
            ? backendArr.length
            : Math.max(normalizedRaw.length, 1);
        getItem = (i) => (backendArr[i] ?? {}) as Record<string, unknown>;
        getRm = (i) => normalizedRaw[i] || normalizedRaw[0];
      }

      nextRows[code] = rows;

      for (let i = 0; i < rows; i++) {
        const rowKey = `row${i}`;
        const item = getItem(i);
        const rm = getRm(i);

        sec.fields.forEach((f) => {
          const key = `${f.name}_${code}_${rowKey}`;

          if (f.type === "RAW_MATERIALS") {
            const fromItem =
              item?.rawMaterialId ?? item?.raw_material_id;
            let rid: string | number = "";
            if (rm?.catalogId != null && rm.catalogId > 0) {
              rid = rm.catalogId;
            } else if (
              fromItem != null &&
              String(fromItem).trim() !== ""
            ) {
              const n = Number(fromItem);
              const allowSaved =
                !validCatalogRawMaterialIds ||
                validCatalogRawMaterialIds.size === 0 ||
                (Number.isFinite(n) && validCatalogRawMaterialIds.has(n));
              if (allowSaved) {
                if (typeof fromItem === "number" && Number.isFinite(fromItem)) {
                  rid = fromItem;
                } else if (typeof fromItem === "string") {
                  rid = fromItem;
                } else {
                  rid = s(fromItem);
                }
              }
            } else if (rm?.id != null && rm.id > 0) {
              rid = rm.id;
            }
            nextValues[key] =
              rid !== "" && rid != null ? String(rid) : "";
            nextValues[`${key}_label`] = s(
              item?.rawMaterialName ?? item?.raw_material_name ?? rm?.name
            );
            return;
          }

          if (!item) {
            nextValues[key] = "";
            return;
          }

          // Map backend field names to form field names
          let value = "";
          
          // A2_TRANSPORT_DETAILS mapping
          if (code === "A2_TRANSPORT_DETAILS") {
            if (f.name === "manufacturingLatitude") {
              value = s(item.manufacturingLat);
            } else if (f.name === "manufacturingLongitude") {
              value = s(item.manufacturingLng);
            } else if (f.name === "state") {
              value = s(item.state);
            } else if (f.name === "district") {
              value = s(item.district);
            } else if (f.name === "pincode") {
              value = s(item.pincode);
            } else if (f.name === "distanceBetweenSites") {
              value = s(item.distanceKm);
            } else {
              value = s(item[f.name]);
            }
          }
          // A2_VEHICLE_DETAILS mapping
          else if (code === "A2_VEHICLE_DETAILS") {
            value = readA2VehicleApiValueForField(f.name, item);
            if (f.name === "vehicle_rawMaterial_01") {
              nextValues[key] = value;
              nextValues[`${key}_label`] = s(
                item?.rawMaterialName ?? item?.raw_material_name ?? rm?.name
              );
              return;
            }
          }
          // A2_FUEL_TRANSPORT mapping
          else if (code === "A2_FUEL_TRANSPORT") {
            value = readA2FuelApiValueForField(f.name, item);
          }
          // A2_EMBODIED_ENERGY mapping
          else if (code === "A2_EMBODIED_ENERGY") {
            if (f.name === "embodiedEnergy_unit") {
              value =
                item.embodiedEnergyUnitId != null &&
                String(item.embodiedEnergyUnitId).trim() !== ""
                  ? String(item.embodiedEnergyUnitId)
                  : "";
            } else {
              value = s(item[f.name]);
            }
          }
          // A2_EMBODIED_CARBON mapping
          else if (code === "A2_EMBODIED_CARBON") {
            if (f.name === "characterizationFactor_unit") {
              value =
                item.characterizationFactorUnit != null &&
                String(item.characterizationFactorUnit).trim() !== ""
                  ? String(item.characterizationFactorUnit)
                  : "";
            } else if (f.name === "embodiedCarbon_referenceQty") {
              value = formatEmbodiedCarbonReferenceQtyFromUnknown(
                item.embodiedCarbonReferenceQty
              );
            } else if (f.name === "embodiedCarbon_referenceQty_unit") {
              value =
                item.embodiedCarbonReferenceUnit != null &&
                String(item.embodiedCarbonReferenceUnit).trim() !== ""
                  ? String(item.embodiedCarbonReferenceUnit)
                  : "";
            } else if (f.name === "embodiedCarbon_functionalUnit") {
              value = formatEmbodiedCarbonFunctionalUnitFromUnknown(
                item.embodiedCarbonFunctionalQty
              );
            } else if (f.name === "embodiedCarbon_functionalUnit_unit") {
              value =
                item.embodiedCarbonFunctionalUnit != null &&
                String(item.embodiedCarbonFunctionalUnit).trim() !== ""
                  ? String(item.embodiedCarbonFunctionalUnit)
                  : "";
            } else {
              value = s(item[f.name]);
            }
          }
          // Default: try direct mapping
          else {
            value = s(item[f.name]);
          }

          nextValues[key] = value;
        });

        if (code === "A2_FUEL_TRANSPORT" && fuelPrefill) {
          const ee = readA2FuelApiValueForField("embodiedEnergy", item);
          const eeU = readA2FuelApiValueForField("embodiedEnergy_unit", item);
          if (ee !== "") nextValues[`embodiedEnergy_${code}_row${i}`] = ee;
          if (eeU !== "") nextValues[`embodiedEnergy_unit_${code}_row${i}`] = eeU;
        }
      }
    });

    setSectionRows((p) => ({ ...p, ...nextRows }));
    setValues((p: any) => ({ ...p, ...nextValues }));
  };

  return { prefillFromSubmission };
}